import React, { useState, useEffect } from 'react';
import { withLDProvider, useLDClient } from 'launchdarkly-react-client-sdk';
import Observability from '@launchdarkly/observability';
import SessionReplay from '@launchdarkly/session-replay';
import { isEqual } from 'lodash';
import SessionStore from 'stores/SessionStore';
import { isProd } from 'helpers/helper_functions';
// Get the client-side ID from environment variables
const clientSideID = `${LAUNCHDARKLY_CLIENT_SIDE_ID}`;
function LDIdentify({ user, children }) {
    const ldClient = useLDClient();
    useEffect(() => {
        var _a, _b;
        if (!ldClient || !user || !user._id) {
            return;
        }
        const context = {
            kind: 'user',
            key: user._id,
            name: user.name || user.email,
            email: user.email,
            userType: user._type,
            roles: (_a = user.roles) !== null && _a !== void 0 ? _a : [],
            tags: (_b = user.tags) !== null && _b !== void 0 ? _b : [],
        };
        ldClient
            .waitForInitialization()
            .then(() => ldClient.identify(context))
            .then(() => {
            ldClient.flush();
            console.log('[LD] Re-identified with updated user context', {
                id: user._id,
                email: user.email,
                userType: user._type,
                roles: user.roles,
            });
        })
            .catch((e) => {
            console.error('[LD] Failed to re-identify user', e);
        });
    }, [ldClient, user]);
    return React.createElement(React.Fragment, null, children);
}
const LaunchDarklyWrapper = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [LDProvider, setLDProvider] = useState(null);
    useEffect(() => {
        const handleSessionChange = () => {
            const currentUser = SessionStore.getUser();
            console.log('[LD] Session changed. User fetched:', {
                hasUser: Boolean(currentUser),
                id: currentUser === null || currentUser === void 0 ? void 0 : currentUser._id,
                email: currentUser === null || currentUser === void 0 ? void 0 : currentUser.email,
                type: currentUser === null || currentUser === void 0 ? void 0 : currentUser._type,
            });
            setUser((prev) => (isEqual(prev, currentUser) ? prev : currentUser));
        };
        // Initial setup
        handleSessionChange();
        // Listen for session changes
        SessionStore.addChangeListener(handleSessionChange);
        return () => {
            SessionStore.removeChangeListener(handleSessionChange);
        };
    }, []);
    useEffect(() => {
        if (user && user._id && !isInitialized) {
            // Initialize LaunchDarkly with user context
            const initializeLaunchDarkly = async () => {
                var _a, _b;
                try {
                    console.log('[LD] Initializing LaunchDarkly...', {
                        hasClientSideID: Boolean(clientSideID),
                        userId: user._id,
                        email: user.email,
                        userType: user._type,
                    });
                    const LDProviderFactory = withLDProvider({
                        clientSideID,
                        context: {
                            kind: 'user',
                            key: user._id,
                            name: user.name || user.email,
                            email: user.email,
                            userType: user._type,
                            roles: (_a = user.roles) !== null && _a !== void 0 ? _a : [],
                            tags: (_b = user.tags) !== null && _b !== void 0 ? _b : [],
                            // Add any other user properties you want to track
                        },
                        options: {
                            plugins: isProd()
                                ? [new Observability(), new SessionReplay()]
                                : [],
                            // Other options...
                        },
                        reactOptions: {
                            useCamelCaseFlagKeys: true,
                            sendEventsOnFlagRead: true,
                        },
                    });
                    // Create a pass-through component so we can use JSX with the HOC
                    const LDProviderWithUser = LDProviderFactory(({ children }) => (React.createElement(React.Fragment, null, children)));
                    setLDProvider(() => LDProviderWithUser);
                    setIsInitialized(true);
                    console.log('[LD] LaunchDarkly initialized for user:', user.email);
                }
                catch (error) {
                    console.error('[LD] Failed to initialize LaunchDarkly:', error);
                    // Continue without LaunchDarkly if initialization fails
                    setIsInitialized(true);
                }
            };
            initializeLaunchDarkly();
        }
    }, [user, isInitialized]);
    // If LaunchDarkly is not initialized yet, render children without it
    if (!isInitialized || !LDProvider) {
        console.log('[LD] Not initialized yet. Rendering children without LD. ', {
            isInitialized,
            hasLDProvider: Boolean(LDProvider),
        });
        return children;
    }
    // Wrap children with LaunchDarkly provider and re-identify on user changes
    return (React.createElement(LDProvider, null,
        React.createElement(LDIdentify, { user: user }, children)));
};
export default LaunchDarklyWrapper;
